import html2canvas from 'html2canvas';
import { prepareLegendClone } from './prepareLegendClone';

// Соотношения сторон листа (для горизонтальной ориентации, W/H).
export const ASPECT_RATIOS = {
    '4:3': 4 / 3,
    '16:9': 16 / 9,
};

// Ориентация листа. Для вертикальной соотношение сторон инвертируется.
export const ORIENTATIONS = {
    'Горизонтальная': 'landscape',
    'Вертикальная': 'portrait',
};

// Угол листа для размещения условных обозначений.
export const LEGEND_POSITIONS = {
    'Справа сверху': 'top-right',
    'Справа снизу': 'bottom-right',
    'Слева сверху': 'top-left',
    'Слева снизу': 'bottom-left',
};
// Итоговое соотношение сторон с учётом ориентации.
const effectiveRatio = (aspect, orientation, source) => {
    const base = ASPECT_RATIOS[aspect] || (source.width / source.height);

    return orientation === 'portrait' ? 1 / base : base;
};

// Множители разрешения (экранное × N). Реальная детализация ограничена
// текущим рендером Mapbox; множитель увеличивает размер итогового PNG.
export const SCALE_OPTIONS = [ 1, 2, 3 ];

// Форматы растра. PNG — без потерь, прозрачность; JPEG — меньше вес, без
// прозрачности (фон заливается белым), с регулировкой качества.
export const FORMATS = {
    png: { mime: 'image/png', ext: 'png' },
    jpeg: { mime: 'image/jpeg', ext: 'jpg' },
};
// Качество JPEG по умолчанию (0..1); регулируется ползунком в диалоге.
export const DEFAULT_JPEG_QUALITY = 0.92;
// Пресеты размера шрифта заголовка (множитель к базовому кеглю).
export const TITLE_FONT_SCALES = {
    'Маленький': 0.7,
    'Средний': 1,
    'Крупный': 1.4,
    'Очень крупный': 1.8,
};
export const DEFAULT_TITLE_FONT_SCALE = 1;
// Пресеты масштаба блока условных обозначений (множитель к базовому размеру).
// Позволяют ужать разросшуюся на несколько колонок легенду или, наоборот, укрупнить.
export const LEGEND_SCALES = {
    'Маленькие': 0.5,
    'Средние': 0.7,
    'Крупные': 1,
    'Очень крупные': 1.3,
};
export const DEFAULT_LEGEND_SCALE = 1;

// Множитель размера наложений (заголовок + легенда) относительно карты.
// Заголовок и условные рендерятся крупнее, чтобы читались на печати.
const OVERLAY_ZOOM = 2;
const TITLE_BASE_FONT = 26 * OVERLAY_ZOOM;
const TITLE_BASE_PAD = 14 * OVERLAY_ZOOM;
const TITLE_LINE_HEIGHT = 1.25;
const LEGEND_MARGIN = 12;
// Темы оформления оверлеев (заголовок + легенда) при экспорте.
// Тёмная — как легенда на странице; светлая — белая полупрозрачная подложка
// с инвертированным (тёмным) текстом, для печати на светлом фоне.
export const OVERLAY_THEMES = {
    'Тёмная': 'dark',
    'Светлая': 'light',
};
const THEME_COLORS = {
    dark: { bg: 'rgba(46, 53, 61, 0.8)', text: '#ffffff' },
    light: { bg: 'rgba(255, 255, 255, 0.85)', text: '#1f2933' },
};
export const themeColors = theme => THEME_COLORS[theme] || THEME_COLORS.dark;
// Прямоугольник со скруглёнными углами.
const roundRectPath = (ctx, x, y, w, h, r) => {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
};
// Разбивка текста на строки по словам в пределах maxWidth.
const wrapLines = (ctx, text, maxWidth) => {
    const words = text.split(/\s+/);
    const lines = [];
    let line = '';

    words.forEach(word => {
        const candidate = line ? `${line} ${word}` : word;
        if (line && ctx.measureText(candidate).width > maxWidth) {
            lines.push(line);
            line = word;
        } else {
            line = candidate;
        }
    });
    if (line) {
        lines.push(line);
    }

    return lines;
};
// Плашка заголовка поверх карты, сверху. leftBound/rightBound — горизонтальные
// границы, в которые плашка должна вписаться (рассчитываются с учётом положения
// легенды, чтобы заголовок её не перекрыл).
const drawTitle = (ctx, text, scale, leftBound, rightBound, fontScale = 1, colors = THEME_COLORS.dark, align = 'left') => {
    const margin = Math.round(LEGEND_MARGIN * scale);
    const pad = Math.round(TITLE_BASE_PAD * scale);
    const fontSize = Math.round(TITLE_BASE_FONT * scale * fontScale);
    const lineH = Math.round(fontSize * TITLE_LINE_HEIGHT);

    ctx.font = `600 ${fontSize}px Arial, sans-serif`;
    ctx.textBaseline = 'top';

    const cardMaxWidth = rightBound - leftBound;
    const textMaxWidth = cardMaxWidth - pad * 2;
    const lines = wrapLines(ctx, text, textMaxWidth);
    const textWidth = Math.max(...lines.map(l => ctx.measureText(l).width));
    const cardW = Math.min(textWidth + pad * 2, cardMaxWidth);
    const cardH = lines.length * lineH + pad * 2;
    // Левый край плашки: при выравнивании вправо — прижимаем к rightBound.
    const cardX = align === 'right' ? rightBound - cardW : leftBound;

    ctx.fillStyle = colors.bg;
    roundRectPath(ctx, cardX, margin, cardW, cardH, Math.round(8 * scale));
    ctx.fill();

    ctx.fillStyle = colors.text;
    lines.forEach((l, i) => {
        ctx.fillText(l, cardX + pad, margin + pad + i * lineH);
    });
};
// Центрированный кроп исходного canvas карты под нужное соотношение сторон.
const cropToAspect = (source, ratio) => {
    let cropW = source.width;
    let cropH = Math.round(cropW / ratio);

    if (cropH > source.height) {
        cropH = source.height;
        cropW = Math.round(cropH * ratio);
    }

    return {
        cropW,
        cropH,
        sx: Math.round((source.width - cropW) / 2),
        sy: Math.round((source.height - cropH) / 2),
    };
};

// Итоговый размер растра (px) для заданного множителя и формата — для подписи
// вариантов разрешения реальными пикселями.
export const getOutputSize = ({ map, scale = 1, aspect = '16:9', orientation = 'landscape' }) => {
    const source = map.getCanvas();
    const ratio = effectiveRatio(aspect, orientation, source);
    const { cropW, cropH } = cropToAspect(source, ratio);

    return {
        width: Math.round(cropW * scale),
        height: Math.round(cropH * scale),
    };
};

/**
 * Собирает итоговый растр из текущего вида карты, опционального заголовка и
 * легенды. Возвращает Promise<Blob> (image/png).
 *
 * @param {Object}  params
 * @param {mapboxgl.Map} params.map           инстанс карты
 * @param {string}  [params.title]            пользовательский заголовок
 * @param {boolean} [params.includeLegend]    наложить условные обозначения
 * @param {number}  [params.scale]            множитель разрешения (1|2|3)
 * @param {string}  [params.aspect]           '4:3' | '16:9'
 * @param {string}  [params.format]           'png' | 'jpeg'
 * @param {number}  [params.quality]          качество JPEG (0..1)
 * @param {number}  [params.titleFontScale]   множитель кегля заголовка
 * @param {number}  [params.legendScale]      множитель размера блока условных
 * @param {string}  [params.orientation]      'landscape' | 'portrait'
 * @param {string}  [params.theme]            тема оверлеев: 'dark' | 'light'
 * @param {string}  [params.legendPosition]   угол легенды: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left'
 */
export const exportMapImage = async ({
    map,
    title = '',
    includeLegend = false,
    scale = 1,
    aspect = '16:9',
    format = 'png',
    quality = DEFAULT_JPEG_QUALITY,
    titleFontScale = DEFAULT_TITLE_FONT_SCALE,
    legendScale = DEFAULT_LEGEND_SCALE,
    orientation = 'landscape',
    theme = 'dark',
    legendPosition = 'top-right',
}) => {
    const colors = themeColors(theme);
    const source = map.getCanvas();
    const ratio = effectiveRatio(aspect, orientation, source);
    const { cropW, cropH, sx, sy } = cropToAspect(source, ratio);
    const titleText = title.trim();
    const fmt = FORMATS[format] || FORMATS.png;
    const out = document.createElement('canvas');
    out.width = Math.round(cropW * scale);
    out.height = Math.round(cropH * scale);

    const ctx = out.getContext('2d');
    // JPEG не поддерживает прозрачность — подкладываем белый фон под кадр.
    if (fmt.mime === 'image/jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, out.width, out.height);
    }
    // Карта на весь холст.
    ctx.drawImage(source, sx, sy, cropW, cropH, 0, 0, out.width, out.height);

    // Легенда — плашкой в выбранном углу. Запоминаем её прямоугольник, чтобы
    // заголовок (сверху) её не перекрыл при длинном тексте.
    const margin = Math.round(LEGEND_MARGIN * scale);
    let legendRect = null;

    if (includeLegend) {
        const legendNode = document.getElementById('legend-grid');

        if (legendNode && legendNode.childElementCount) {
            // Итоговый зум легенды: базовый ×OVERLAY_ZOOM (крупнее карты для
            // читаемости) × пользовательский legendScale.
            const legendZoom = OVERLAY_ZOOM * legendScale;
            // Сколько по вертикали доступно легенде на листе (в CSS-пикселях клона,
            // до апскейла html2canvas). Если условные не помещаются в эту высоту —
            // prepareLegendClone разложит их в несколько колонок (перенос целыми
            // группами слоёв). Отнимаем поля сверху и снизу. Чем крупнее legendScale,
            // тем меньше CSS-высота — и тем охотнее легенда уходит в колонки.
            const legendMaxHeightPx = (out.height - margin * 2) / (scale * legendZoom);
            const legendCanvas = await html2canvas(legendNode, {
                // Полупрозрачную подложку рисует сам клон (prepareLegendClone).
                backgroundColor: null,
                // Легенда крупнее карты (базовый зум) × пользовательский масштаб.
                scale: scale * legendZoom,
                logging: false,
                onclone: clonedDoc => prepareLegendClone(clonedDoc.getElementById('legend-grid'), { theme, clonedDoc, maxHeightPx: legendMaxHeightPx }),
            });
            const onLeft = legendPosition.indexOf('left') !== -1;
            const onTop = legendPosition.indexOf('bottom') === -1;
            const lw = legendCanvas.width;
            const lh = legendCanvas.height;
            const lx = onLeft ? margin : Math.max(margin, out.width - lw - margin);
            const ly = onTop ? margin : Math.max(margin, out.height - lh - margin);

            ctx.drawImage(legendCanvas, lx, ly);
            legendRect = { lx, lw, onLeft, onTop };
        }
    }

    // Заголовок — плашкой поверх карты сверху. Если легенда в верхнем углу,
    // ужимаем горизонтальные границы заголовка, чтобы он её не перекрыл:
    // легенда слева → заголовок правее неё; легенда справа → заголовок левее.
    if (titleText) {
        let leftBound = margin;
        let rightBound = out.width - margin;
        // Легенда слева сверху → заголовок правее неё и прижат к правому краю листа;
        // легенда справа сверху → заголовок ограничен её левым краем (слева).
        let align = 'left';

        if (legendRect && legendRect.onTop) {
            if (legendRect.onLeft) {
                leftBound = legendRect.lx + legendRect.lw + margin;
                align = 'right';
            } else {
                rightBound = legendRect.lx - margin;
            }
        }

        drawTitle(ctx, titleText, scale, leftBound, rightBound, titleFontScale, colors, align);
    }

    return new Promise(resolve => out.toBlob(resolve, fmt.mime, quality));
};

// Скачивание Blob как файла.
export const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
